import { useEffect, useMemo, useState } from 'react';
import { useSession } from '../session/useSession';
import { forgetCredential } from '../session/fragment';
import { errorText } from '../protocol/wire';
import { buildTable } from '../model/table';
import { buildChoices } from '../model/choices';
import { isV2, loadPresentation, type PresentationResult } from '../presentation/load';
import { Table } from '../components/Table';
import { SceneTable } from '../scene/SceneTable';
import type { SeatCredential } from '../session/fragment';
import { refreshArt } from '../art/assets';
import { GameDisplay } from '../display/GameDisplay';
import { LayoutNotice, type LayoutApi } from '../display/layouts';
import { chooseVariant, defaultLayoutId, layoutOptions, layoutUrl, readStoredLayout, requestedLayout, storeLayout } from '../presentation/variants';
import { canUseSpectatorBoard } from '../display/spectatorScene';
import { presentationRenderers } from '../boards/runtime/rendererPreference';

export function App({ spectator = false, credential, paused = false }: { spectator?: boolean; credential?: SeatCredential | null; paused?: boolean }) {
  const { session, state: liveState } = useSession(spectator, credential, paused);
  const state = { ...liveState, paused: liveState.paused || paused };
  const [presentation, setPresentation] = useState<PresentationResult>({ status: 'none' });
  // Player-selectable layouts (docs/ui-variants-design.md): ?ui=<id>, then the remembered choice, then the host's default.
  const options = useMemo(() => layoutOptions(state.presentationVariants), [state.presentationVariants]);
  const [picked, setPicked] = useState<string | null>(null);
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const layout = useMemo(
    () => chooseVariant(options, picked ?? requestedLayout(window.location.search), readStoredLayout(state.gameId), defaultLayoutId(options, state.presentation)),
    [options, picked, state.gameId, state.presentation],
  );
  const chosen = options.find(o => o.id === layout.id)?.presentation ?? state.presentation;
  const layouts: LayoutApi | undefined = options.length > 0 && layout.id ? {
    options, current: layout.id,
    select: id => {
      setPicked(id); setNoticeDismissed(true);
      if (state.gameId) storeLayout(state.gameId, id);
      window.history.replaceState(window.history.state, '', layoutUrl(window.location, id));
    },
  } : undefined;
  const presentationKey = chosen ? `${chosen.sha256}/${chosen.module?.sha256 ?? ''}` : null;
  const [artStatus, setArtStatus] = useState('');
  const [artBusy, setArtBusy] = useState(false);
  const updateArt = async () => {
    if (artBusy || presentation.status !== 'ready' || !isV2(presentation.presentation)) return;
    const original = presentation.presentation;
    setArtBusy(true);
    try {
      const updated = await refreshArt(original);
      setPresentation(current => current.status === 'ready' && current.presentation === original ? { status: 'ready', presentation: updated } : current);
      setArtStatus(updated === original ? 'Artwork is current.' : 'Artwork updated.');
    } catch { setArtStatus('Artwork update unavailable. Keeping the current artwork.'); }
    finally { setArtBusy(false); }
  };
  useEffect(() => {
    if (new URLSearchParams(window.location.search).get('artWatch') !== '1') return;
    const timer = setInterval(() => { void updateArt(); }, 2500);
    return () => clearInterval(timer);
  }, [presentation, artBusy]);

  useEffect(() => {
    let cancelled = false;
    if (!chosen) { setPresentation({ status: 'none' }); return; }
    void loadPresentation(chosen, state.gameId).then(result => { if (!cancelled) setPresentation(result); });
    return () => { cancelled = true; };
    // Reload only when the announced (or chosen) package changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [presentationKey]);

  const table = useMemo(
    () => (state.observation ? buildTable(state.observation, state.description, state.seat, { matchId: state.matchId, profiles: state.profiles }) : null),
    [state.observation, state.description, state.seat, state.matchId, state.profiles],
  );
  const choices = useMemo(
    () => (state.observation && table ? buildChoices(state.observation, state.actionSchemas, new Set(table.objects.keys())) : []),
    [state.observation, state.actionSchemas, table],
  );

  if (state.fatal) {
    return (
      <main className="screen-message" role="alert">
        <h1>Can't join this game</h1>
        <p>{errorText(state.fatal)}</p>
        <button type="button" className="button" onClick={() => { forgetCredential(window.sessionStorage, window.location.pathname); window.location.href = window.location.pathname.replace(/\/play$/, '/watch'); }}>
          Watch as a spectator instead
        </button>
      </main>
    );
  }
  if (!table) {
    return (
      <main className="screen-message" aria-busy="true">
        <div className="spinner" aria-hidden="true" />
        <p>{state.connection === 'reconnecting' ? 'Reconnecting to the game host…' : 'Connecting to the game host…'}</p>
      </main>
    );
  }
  const ready = presentation.status === 'ready' && table.interfaceVersion === 'ui-2' ? presentation.presentation : null;
  const notice = presentation.status === 'fallback' ? `Using the standard layout. ${presentation.reason}` : null;
  const unknownLayout = layout.unknown && !noticeDismissed ? <LayoutNotice requested={layout.unknown} onDismiss={() => setNoticeDismissed(true)} /> : null;
  const boardRenderers = presentationRenderers(isV2(ready) ? ready.boards?.boards : undefined, isV2(ready) ? ready.manifest.requires : []);
  if (isV2(ready)) return <GameDisplay spectator={spectator || state.seat === 'spectator'} boardCapable={canUseSpectatorBoard(ready)} renderers={boardRenderers.available} declaredRenderer={boardRenderers.declared} layouts={layouts} art={ready.manifest.artRelease ? { refresh: () => void updateArt(), busy: artBusy, status: artStatus || ready.artNotice || '' } : undefined}>
    {unknownLayout}
    <SceneTable key={`${state.matchId}/${state.seat}/${layout.id ?? ''}`} session={session} state={state} presentation={ready} fallbackTable={table} />
  </GameDisplay>;
  return <GameDisplay spectator={spectator || state.seat === 'spectator'} boardCapable={false} layouts={layouts}>{unknownLayout}<Table session={session} state={state} table={table} choices={choices} presentation={ready} presentationNotice={notice} /></GameDisplay>;
}
